import {GROUND_BACKGROUNDS} from '../groundBackgrounds';

/**
 * БОЛОТНЫЕ ОГОНЬКИ — эффект болотной подложки (bog_1).
 *
 * Над темной водой трясины висит блуждающий огонек: бледное ядро в зеленом
 * ореоле. Он не летает — загорается, несколько секунд тлеет, едва сползая в
 * сторону, и гаснет; а рядом в этот миг загорается следующий. Так огонек
 * «перебирается» по болоту цепочкой в три-четыре шага (см.
 * utils/effects/biome/willOWisps).
 *
 * РЕШЕНИЯ ВЛАДЕЛЬЦА ИГРЫ (он смотрел огоньки в игре):
 *  - огоньки разного размера и в среднем на 30 % мельче прежних;
 *  - их чуть больше, чем было;
 *  - горят они НАД жетонами строений и гор, над значками отрядов и их
 *    шевронами, а не под ними.
 *
 * Чем огонек НЕ должен стать:
 *  - светлячком: едва он полетит через несколько ячеек, это уже фея волшебного
 *    леса. Поэтому он почти стоит на месте и живет долго;
 *  - бликом: мигание короче секунды — это соль и море;
 *  - меткой ячейки: зеленое свечение размером с ячейку читалось бы как
 *    подсветка хода. Поэтому ореол меньше ячейки, не сидит по ее центру и все
 *    время дышит.
 *
 * Зеленая точка на зеленой тине видна только яркостью, поэтому ядро почти
 * белое, а зелень — в ореоле.
 */
export const WILL_O_WISPS_KIND = 'willOWisps';

export const WILL_O_WISPS_EFFECT = Object.freeze({
  kind: WILL_O_WISPS_KIND,
  backgrounds: Object.freeze({
    [GROUND_BACKGROUNDS.BOG_1]: Object.freeze({
      // Огонек горит НАД всем полем: над жетонами строений и гор, над значками
      // отрядов и их шевронами (решение владельца игры: раньше строения и
      // отряды его закрывали). На этом слое лежит весь огонек — и ядро, и ореол.
      // Слой светящийся: трясина темная, свет на ней — самый заметный из всех.
      // Жетон под огоньком читается: свет прибавляется к картинке, а не
      // закрывает ее, ядро мелкое, ореол прозрачный (см. wisp)
      layer: 'AIR_GLOW',
      // ЦЕПОЧКИ НАД ПОЛЕМ: вся середина пейзажа — открытая трясина, огоньки
      // загораются на игровых ячейках там, куда смотрит камера
      field: {
        firstDelayMs: {min: 1200, max: 3500},
        // Пауза — от начала предыдущей цепочки; цепочка живет 17–35 секунд,
        // так что над полем их почти всегда столько, сколько разрешает
        // maxGroups. Было три, стало четыре: это и есть «огоньков чуть больше» —
        // разом над полем горит на одну цепочку больше
        intervalMs: {min: 4500, max: 8000},
        maxGroups: 4,
        // Поле не начинается пустым: столько цепочек уже тлеет к началу партии
        prewarm: 2,
        // Огонек загорается не по центру ячейки: смещение от центра — до
        // стольких размеров ячейки в каждую сторону
        offCentreFactor: 0.8,
        // Новая цепочка — не ближе стольких размеров ячейки к уже горящим
        apartFactor: 5,
      },
      // ЦЕПОЧКИ ВОКРУГ ПОЛЯ: над открытой водой пейзажа. Точка годится, пока
      // на ней нет игровых ячеек: над ячейками горят цепочки поля
      margin: {
        firstDelayMs: {min: 3000, max: 8000},
        intervalMs: {min: 9000, max: 16000},
        maxGroups: 2,
        prewarm: 1,
        // Покрытие поля в точке выше этого — точка под ячейками, не годится
        fieldCoverageLimit: 0.4,
        // Точка, которую камера не видит, выбирается во столько раз реже
        offViewWeight: 0.25,
        // Открытая темная вода на картинке пейзажа (доли ее стороны). Светлой
        // воды в правом верхнем углу здесь нет нарочно: на отражении неба
        // светящийся огонек не виден
        anchors: [
          // Серая вода к северу от поля
          {u: 0.5, v: 0.2, radiusU: 0.03},
          // Вода у тростника, правее и выше поля
          {u: 0.66, v: 0.3, radiusU: 0.03},
          // Трясина левее и выше поля
          {u: 0.3, v: 0.3, radiusU: 0.03},
          // Самая темная вода, правее и ниже поля
          {u: 0.68, v: 0.7, radiusU: 0.03},
          // Темные окна трясины в середине пейзажа: в дело идут на полях
          // другой формы, где эти места не заняты ячейками
          {u: 0.52, v: 0.32, radiusU: 0.03},
          {u: 0.4, v: 0.42, radiusU: 0.03},
          {u: 0.6, v: 0.45, radiusU: 0.03},
          {u: 0.47, v: 0.55, radiusU: 0.03},
          {u: 0.56, v: 0.63, radiusU: 0.03},
          {u: 0.38, v: 0.66, radiusU: 0.03},
        ],
      },
      // ЦЕПОЧКА: огонек гаснет здесь — загорается рядом
      chain: {
        // Огоньков в цепочке (было 2–4). Разом в цепочке горит один огонек,
        // на стыке — два: длина цепочки — это то, как далеко огонек уходит по
        // болоту, а не то, сколько огоньков видно разом. Не меньше трех: в
        // цепочке должно хватать огоньков и на мелкий, и на крупный
        count: {min: 3, max: 4},
        // Следующий огонек — на таком расстоянии от гаснущего, размеров ячейки
        // (ячейка — два размера в ширину: от двух третей ячейки до полутора)
        stepFactor: {min: 1.4, max: 2.8},
        // Цепочка не мечется: каждый шаг отклоняется от предыдущего не больше
        turnDeg: 50,
        // Каждая пятая цепочка — холодная, бирюзовая
        coldShare: 0.2,
      },
      // ОГОНЕК: ореол и ядро поверх него
      wisp: {
        // Разгорается, тлеет и гаснет — без вспышек
        fadeInMs: 1200,
        holdMs: {min: 4000, max: 7000},
        fadeOutMs: 1800,
        // РАЗМЕР У КАЖДОГО ОГОНЬКА СВОЙ. Ядро: кадр пятна в размерах ячейки,
        // от самого мелкого до самого крупного — в два с половиной раза.
        // Середина разброса, 0.6125, — это 0.7 среднего размера прежних
        // огоньков (0.875): «в среднем на 30 % мельче». Самый крупный — с
        // прежний средний. Яркая середина пятна — около трети кадра: на телефоне
        // это от двух точек экрана у самого мелкого до шести у самого крупного
        coreSizeFactor: {min: 0.35, max: 0.875},
        // Ядро чуть прозрачнее прежнего (было 0.95): теперь оно горит над
        // жетонами, и на светлом жетоне плотное ядро выжигало бы белое пятно
        coreAlpha: 0.9,
        // Ореол растет вместе с ядром: его кадр шире кадра ядра во столько раз
        // (как у прежних огоньков: 2.65 к 0.875). Видимое свечение — около
        // половины кадра, то есть меньше ячейки даже у самого крупного
        haloToCoreRatio: 3.03,
        // Ореол прозрачнее прежнего (было 0.34): над светлым жетоном он не
        // должен забеливать его, а на темной воде виден и такой
        haloAlpha: 0.26,
        // Пока разгорается и гаснет, огонек еще и растет от этой доли размера
        kindleScale: 0.55,
        // Мерцание: яркость дрожит быстро и плывет медленно, сумма долей — 1
        flicker: {
          base: 0.78,
          fast: {depth: 0.14, periodMs: 900},
          slow: {depth: 0.08, periodMs: 2300},
        },
        // Ореол дышит: размер ходит на эту долю
        breath: {depth: 0.08, periodMs: 3000},
        // За всю жизнь огонек сползает по ленивой дуге на столько размеров
        // ячейки (меньше одной ячейки), дуга выгнута в сторону на bendFactor
        travelFactor: {min: 0.7, max: 1.7},
        bendFactor: {min: 0.15, max: 0.45},
      },
      // Краски: ядро почти белое, зелень — в ореоле
      palettes: {
        warm: {core: 0xeaffc8, halo: 0x86f06a},
        cold: {core: 0xd8fff0, halo: 0x5fe0b0},
      },
    }),
  }),
});
